import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import { formattedDate } from '../../../../../utils/dateUtils';
import { permissionChecks } from '../../../../../utils/permission-check';
import { ProjectGeodataManager } from '../../../../../viewer/project-geodata-manager';
import { TimelineContainer } from '../../../../../viewer/timeline-manager';
import { viewerZIndices } from '../../../../../viewer/viewer-z-indices';
import { useActiveTimelineContainer } from '../../../../hooks/use-active-timeline-container';
import { useFeatureFlag } from '../../../../hooks/use-feature-flag';
import { dialogZIndex } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { SkyviewContext } from '../../../skyview/skyview-state';
import { Tooltip } from '../../../tooltip/tooltip';

/** A card's width, and the dashed link to the next. All are as wide, so only the shown render. */
const itemWidth = 112;
const linkWidth = 18;
const itemStride = itemWidth + linkWidth;
/** How many cards to render beyond the shown on each side, so they're there when scrolled to. */
const overscan = 4;
/** How much of the shown cards a scroll button scrolls past. */
const scrollPage = 0.8;
/** The radius of the notch's concave corners, where it meets the top edge. */
const cornerRadius = 12;
/** Where the panel opens: below the share and On demand chips at the top, their margin and height. */
const panelTop = 16 + 36 + 10;
/** The panel's widest, so the timelines stay together on very wide screens. */
const maxPanelWidth = 1400;
/** The panel's padding around the timelines and the buttons. */
const panelPadding = 4;

/**
 * The timeline at the top of SkyView, in place of the toolbar's: a notch on the top edge with the
 * active timeline container. Hovered, focused or clicked, all of them open below it, oldest first,
 * in front of the dialogs and the orientation cube, and stay open until something outside it is used.
 */
const TimelineBar = () => {
  const enabled = useFeatureFlag('SK-7562-timeline-bar');
  const { activeScene, skyview } = React.useContext(SkyviewContext);
  const activeTimelineContainer = useActiveTimelineContainer();

  if (
    !enabled ||
    !activeTimelineContainer ||
    activeScene === 'panoramaViewer' ||
    !permissionChecks.hasAccessToFeature('feature:viewer:toolbar:timeline')
  ) {
    return null;
  }

  return (
    <TimelineBarContent
      activeTimelineContainer={activeTimelineContainer}
      onSelect={(container) => skyview.setActiveContainer(container.id)}
    />
  );
};

type ContentProps = {
  activeTimelineContainer: TimelineContainer;
  onSelect: (container: TimelineContainer) => void;
};

const TimelineBarContent = ({ activeTimelineContainer, onSelect }: ContentProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const [view, setView] = React.useState({ scrollLeft: 0, width: 0 });
  /** The width of the viewer between the menus, that the panel opens in. */
  const [areaWidth, setAreaWidth] = React.useState(0);
  const componentRef = React.useRef<HTMLDivElement>(null);
  const panelAreaRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  // The panel is outside the component on the page, in front of everything, but still part of it
  const isInside = (node: Node | null) =>
    !!node && (!!componentRef.current?.contains(node) || !!panelRef.current?.contains(node));

  const containers = ProjectGeodataManager.instance().getTimelineContainers().reverse();
  const activeIndex = containers.findIndex((x) => x.id === activeTimelineContainer.id);
  const trackWidth = containers.length * itemStride - linkWidth;

  const first = Math.max(0, Math.floor(view.scrollLeft / itemStride) - overscan);
  const last = Math.min(
    containers.length - 1,
    Math.floor((view.scrollLeft + view.width) / itemStride) + overscan,
  );

  const updateView = () => {
    const list = listRef.current;
    if (list) {
      setView({ scrollLeft: list.scrollLeft, width: list.clientWidth });
    }
  };

  // Opens below the notch, as wide as the viewer between the menus, and follows it as the window or
  // the menus change size. Placed right away, so the list below is measured where it opens.
  React.useLayoutEffect(() => {
    const component = componentRef.current;
    const area = panelAreaRef.current;
    if (!expanded || !component || !area) {
      return;
    }
    const updatePanelArea = () => {
      const rect = component.getBoundingClientRect();
      area.style.left = `${rect.left}px`;
      area.style.top = `${rect.top + panelTop}px`;
      area.style.width = `${rect.width}px`;
      setAreaWidth(rect.width);
      updateView();
    };
    updatePanelArea();
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updatePanelArea);
    observer?.observe(component);
    window.addEventListener('resize', updatePanelArea);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updatePanelArea);
    };
  }, [expanded]);

  // Opens already scrolled to the active timeline container, so nothing slides into place after
  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (expanded && list) {
      list.scrollLeft = activeIndex * itemStride - (list.clientWidth - itemWidth) / 2;
      updateView();
    }
    // Only when it opens, not as the active one changes while it's open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Stays open, also after a timeline is chosen, until something outside it is used, such as the
  // viewer. The capture phase sees the press even where the viewer stops it.
  React.useEffect(() => {
    if (!expanded) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!isInside(event.target as Node)) {
        setExpanded(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [expanded]);

  const scrollTo = (left: number) => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left, behavior: 'smooth' });
    } else {
      list.scrollLeft = left;
    }
  };

  const scrollPages = (pages: number) => {
    const list = listRef.current;
    if (list) {
      scrollTo(list.scrollLeft + pages * scrollPage * list.clientWidth);
    }
  };

  const atStart = view.scrollLeft <= 0;
  const atEnd = view.scrollLeft + view.width >= trackWidth - 1;
  // With only a few, the panel is only as wide as they are, without the buttons to scroll. Whether
  // they fit is up to the room without the buttons, since the list is narrower with them.
  const scrollable = trackWidth > Math.min(areaWidth - 2 * panelPadding, maxPanelWidth) + 1;

  return (
    <Component
      aria-label={t('timeline.title', { ns: 'skyviewToolbar' })}
      ref={componentRef}
      role="navigation"
      onBlur={(event) => {
        // Only when the focus moves on to something outside, as with Tab. A click elsewhere is
        // handled above, and a click inside on something that takes no focus keeps it open.
        if (event.relatedTarget && !isInside(event.relatedTarget as Node)) {
          setExpanded(false);
        }
      }}
      onFocus={() => setExpanded(true)}
      onMouseEnter={() => setExpanded(true)}
    >
      <Notch aria-expanded={expanded} type="button" onClick={() => setExpanded(true)}>
        <Icon icon={['far', 'clock']} />
        <strong>{activeTimelineContainer.containerIndex}</strong>
        <Separator />
        <span>{formattedDate(activeTimelineContainer.collectionDate)}</span>
      </Notch>

      {expanded &&
        // In the page's body, as the dialogs are. Opened after them, it is in front of those open
        ReactDOM.createPortal(
          <PanelArea ref={panelAreaRef}>
            <Panel ref={panelRef}>
              {scrollable && (
                <>
                  <ScrollButton
                    aria-label={t('timeline.first', { ns: 'skyviewToolbar' })}
                    disabled={atStart}
                    type="button"
                    onClick={() => scrollTo(0)}
                  >
                    <Icon icon={['fas', 'chevrons-left']} />
                  </ScrollButton>
                  <ScrollButton
                    aria-label={t('timeline.earlier', { ns: 'skyviewToolbar' })}
                    disabled={atStart}
                    type="button"
                    onClick={() => scrollPages(-1)}
                  >
                    <Icon icon={['fas', 'chevron-left']} />
                  </ScrollButton>
                </>
              )}
              <List
                ref={listRef}
                role="list"
                onScroll={updateView}
                onWheel={(event) => {
                  // A mouse wheel only scrolls vertically, which scrolls the timelines sideways
                  if (listRef.current && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                    listRef.current.scrollLeft += event.deltaY;
                  }
                }}
              >
                <Track style={{ width: trackWidth }}>
                  {containers.slice(first, last + 1).map((container, i) => (
                    <ItemPosition
                      $last={first + i === containers.length - 1}
                      key={container.id}
                      role="listitem"
                      style={{ left: (first + i) * itemStride }}
                    >
                      <TimelineBarItem
                        container={container}
                        current={container.id === activeTimelineContainer.id}
                        onClick={() => onSelect(container)}
                      />
                    </ItemPosition>
                  ))}
                </Track>
              </List>
              {scrollable && (
                <>
                  <ScrollButton
                    aria-label={t('timeline.later', { ns: 'skyviewToolbar' })}
                    disabled={atEnd}
                    type="button"
                    onClick={() => scrollPages(1)}
                  >
                    <Icon icon={['fas', 'chevron-right']} />
                  </ScrollButton>
                  <ScrollButton
                    aria-label={t('timeline.last', { ns: 'skyviewToolbar' })}
                    disabled={atEnd}
                    type="button"
                    onClick={() => scrollTo(trackWidth)}
                  >
                    <Icon icon={['fas', 'chevrons-right']} />
                  </ScrollButton>
                </>
              )}
            </Panel>
          </PanelArea>,
          document.body,
        )}
    </Component>
  );
};

type ItemProps = {
  container: TimelineContainer;
  current: boolean;
  onClick: () => void;
};

/** A timeline container's number and date, and the data it has below. Its comment on hover. */
const TimelineBarItem = ({ container, current, onClick }: ItemProps) => {
  const { t } = useTranslation();

  const data: { icon: IconProp; label: string }[] = [];
  if (container.pointCloud) {
    data.push({
      icon: ['fal', 'cloud'],
      label: t('viewSettings.layers.pointCloud', { ns: 'skyviewToolbar' }),
    });
  }
  if (container.orthotile) {
    data.push({
      icon: ['fal', 'image'],
      label: t('viewSettings.layers.orthophoto', { ns: 'skyviewToolbar' }),
    });
  }
  if (container.tiledModelId || container.scan) {
    data.push({
      icon: ['fal', 'cube'],
      label: t('viewSettings.layers.tiledModel', { ns: 'skyviewToolbar' }),
    });
  }

  const item = (
    <Item
      $current={current}
      aria-current={current ? 'true' : undefined}
      type="button"
      onClick={onClick}
    >
      <Heading>
        <Index>{container.containerIndex}</Index>
        <DateText>{formattedDate(container.collectionDate)}</DateText>
      </Heading>
      <Data>
        {data.length > 0 ? (
          data.map((x) => <Icon icon={x.icon} key={x.label} title={x.label} />)
        ) : (
          <NoData>{t('timeline.noData', { ns: 'skyviewToolbar' })}</NoData>
        )}
      </Data>
    </Item>
  );

  return container.comment ? (
    <Tooltip
      content={t('timeline.comment', { ns: 'skyviewToolbar', comment: container.comment })}
      mainAxisOffset={8}
      placement="bottom"
    >
      {item}
    </Tooltip>
  ) : (
    item
  );
};

const reveal = keyframes`
  from {
    opacity: 0;
    clip-path: inset(0 50% 0 50% round 14px);
  }
  to {
    opacity: 1;
    clip-path: inset(0 0 0 0 round 14px);
  }
`;

// For the notch, and the panel, which is elsewhere on the page
const colors = css`
  --surface: #fff;
  --text: #37424a;
  --hover: #eef0f0;
  --current: #37424a;
  --current-text: #fff;
  --link: #a5acaf;
  --separator: rgba(55, 66, 74, 0.25);

  font-family: 'Montserrat';
  color: var(--text);
`;

const Component = styled.div`
  ${colors}

  // As wide as the viewer between the menus, but only the notch and the panel take clicks, so the
  // chips beside the notch can still be used
  position: absolute;
  top: 0;
  left: 16px;
  right: 16px;
  z-index: ${viewerZIndices.toolbar};
  pointer-events: none;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Hangs from the top edge, which its concave corners blend into. Its shadow is cut off above, so
// no seam shows along the edge.
const Notch = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 8px;

  font: inherit;
  font-size: 12px;
  font-feature-settings: 'tnum';
  white-space: nowrap;
  color: inherit;
  background-color: var(--surface);
  border: none;
  border-radius: 0 0 14px 14px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  clip-path: inset(0 -24px -24px -24px);
  cursor: pointer;
  pointer-events: auto;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: ${cornerRadius}px;
    height: ${cornerRadius}px;
  }

  &::before {
    left: -${cornerRadius}px;
    background: radial-gradient(
      circle at 0 100%,
      transparent ${cornerRadius - 0.5}px,
      var(--surface) ${cornerRadius}px
    );
  }

  &::after {
    right: -${cornerRadius}px;
    background: radial-gradient(
      circle at 100% 100%,
      transparent ${cornerRadius - 0.5}px,
      var(--surface) ${cornerRadius}px
    );
  }

  strong {
    font-weight: 800;
  }
`;

const Separator = styled.span`
  width: 1px;
  height: 12px;
  background-color: var(--separator);
`;

// Over the viewer between the menus, below the chips so it doesn't cover them. Only the panel in
// it takes clicks.
const PanelArea = styled.div`
  position: fixed;
  z-index: ${dialogZIndex};
  display: flex;
  justify-content: center;
  pointer-events: none;
  ${colors}
`;

// As wide as the timelines, up to the width of the viewer between the menus, and a limit for wide
// screens
const Panel = styled.div`
  min-width: 0;
  max-width: ${maxPanelWidth}px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: ${panelPadding}px;
  pointer-events: auto;

  background-color: var(--surface);
  border-radius: 14px;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  animation: ${reveal} 0.22s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const List = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
`;

// Centered when the timelines don't fill the panel
const Track = styled.div`
  position: relative;
  height: 44px;
  margin-inline: auto;
`;

// The dashed link to the next timeline, so the row reads as one line through time
const ItemPosition = styled.div<{ $last: boolean }>`
  position: absolute;
  top: 0;
  width: ${itemWidth}px;

  ${(props) =>
    !props.$last &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: -${linkWidth}px;
        width: ${linkWidth}px;
        border-top: 2px dashed var(--link);
      }
    `}
`;

const Heading = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  font-feature-settings: 'tnum';
`;

const Index = styled.span`
  font-weight: 500;
  opacity: 0.6;
`;

const DateText = styled.span`
  font-weight: 600;
`;

const Data = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  height: 15px;
  font-size: 13px;
`;

const NoData = styled.span`
  font-size: 10px;
  font-style: italic;
  opacity: 0.6;
`;

const Item = styled.button<{ $current: boolean }>`
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 4px;
  width: 100%;
  height: 44px;
  padding: 0 8px;

  font: inherit;
  white-space: nowrap;
  color: inherit;
  background-color: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background-color: var(--hover);
  }

  ${(props) =>
    props.$current &&
    css`
      color: var(--current-text);

      &,
      &:hover {
        background-color: var(--current);
      }
    `}
`;

const ScrollButton = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 34px;
  height: 44px;

  font-size: 15px;
  color: inherit;
  background-color: transparent;
  border: none;
  border-radius: 10px;
  opacity: 0.7;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 1;
    background-color: var(--hover);
  }

  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
`;

export { TimelineBar };
